import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ButtonForm } from 'Components/Form/styles';
import { Column, Row } from 'Components/Grid';

import { useQuery } from 'react-query';
import { deletePolicyVersion, getPolicyVersionsByPolicyId } from 'services/policies-service';
import { Main as Container } from 'Components/Main';
import { StepperForm } from 'Components/StepperForm';
import { PageLoaderList } from 'Components/PageLoader/List';
import { NoData } from 'Components/NoData';
import { DefaultTable } from 'Components/common/DefaultTable';
import { tableHeader } from './table-header';
import { useAuthUser } from 'store/auth';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Dialog, PageHeader, PlusSvg } from 'Components/ui';

export const PolicyVersions = () => {
  const { t } = useTranslation(['policy']);
  const { id } = useParams();
  const navigate = useNavigate();
  const user = useSelector(useAuthUser);
  const [open, setOpen] = useState(false);
  const [refetch, setRefetch] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [activeStep] = useState(1);

  const steps = [t('policy:versions.steps.details'), t('policy:versions.steps.versions')];

  const {
    isLoading,
    error,
    data: policyVersions,
  } = useQuery(['getPolicyVersionsByPolicyId', refetch], async () => {
    const result = await getPolicyVersionsByPolicyId(id);
    if (result?.data) {
      return result?.data;
    }
    return [];
  });
  const [policyVersionPosition, setPolicyVersionPosition] = useState(null);

  const onDelete = (item, position) => {
    setItemToDelete(item);
    setPolicyVersionPosition(position);
    setOpen(true);
  };

  const onClose = () => {
    setItemToDelete(null);
    setPolicyVersionPosition(null);
    setOpen(false);
  };

  const onConfirm = async () => {
    try {
      const result = await deletePolicyVersion(id, policyVersionPosition);
      if (result.status === 204) {
        onClose();
        setRefetch(!refetch);
        Dialog.alert({
          title: t('policy:versions.delete.successTitle'),
          message: t('policy:versions.delete.success'),
          icon: 'success',
        });
      } else {
        throw new Error(t('policy:versions.delete.errorText'));
      }
    } catch (error) {
      Dialog.alert({
        title: t('policy:versions.delete.errorTitle'),
        message: error.response.data.message ?? error.message,
        icon: 'error',
      });
    }
  };

  const showAddButton = policyVersions => {
    if (policyVersions.length === 0) return true;
    if (policyVersions[policyVersions.length - 1].status === 'Publicado') return true;
    return false;
  };

  return (
    <Container>
      <PageHeader
        title={t('policy:versions.title')}
        segments={[
          { label: t('common:breadcrumbs.compliance') },
          { label: t('policy:page.title'), route: '/policies' },
          { label: t('policy:versions.steps.versions') },
        ]}
      />
      <StepperForm activeStep={activeStep} steps={steps} />
      {isLoading && <PageLoaderList></PageLoaderList>}
      {error && <div>{t('policy:versions.error')} {error.message}</div>}
      {!isLoading && !error && (
        <>
          <div className="card-content">
            <Row justifyContent="center">
              <Column xs={6} className="d-flex">
                <ButtonForm
                  variant="secondary"
                  id="back"
                  onClick={() => navigate(`/policies/edit/${id}`)}
                >
                  {t('policy:versions.back')}
                </ButtonForm>
              </Column>
              <Column xs={6} justifyContent="end" className="d-flex">
                {showAddButton(policyVersions) && (
                  <ButtonForm
                    variant="secondary"
                    startIcon={<PlusSvg />}
                    onClick={() => navigate(`/policies/${id}/versions/add`)}
                  >
                    {t('policy:versions.titleAdd')}
                  </ButtonForm>
                )}
              </Column>
            </Row>
          </div>
          {policyVersions?.length === 0 && (
            <NoData xs={12} md={12} lg={12} description={t('policy:versions.noData')} />
          )}
          {policyVersions?.length > 0 && (
            <Row>
              <Column xs={12} className="px-2 mt-3">
                <DefaultTable
                  rows={tableHeader(t)}
                  cells={policyVersions}
                  onClickDelete={onDelete}
                  userId={user.userId}
                  id={id}
                  link="policies"
                  onClickEdit={position => {
                    navigate(`/policies/${id}/versions/edit/${position}`);
                  }}
                />
                <Dialog
                  open={open}
                  onConfirm={onConfirm}
                  onCancel={onClose}
                  message={`Tem certeza que deseja excluir ${itemToDelete?.version} Versão da Política?`}
                />
              </Column>
            </Row>
          )}
        </>
      )}
    </Container>
  );
};
